import React, {useCallback, useState} from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {useDispatch, useSelector} from "react-redux";
import {
  getIsPromocodeActivated,
  getFormattedPromocodeSavedAmount
} from "@awe-web/shared/lib";
import {Translation as T, useTranslation} from "@awe-web/shared/lib";
import {TextInput, Button, ErrorMessage, InputLabel} from "../../components/form.js";
import {InlineLoadingIndicator} from "../../components/loading_indicator.jsx";
import {PriceBox, PriceLineup} from "./price";
import {getPromocodeActivationErrorMessage} from "../store.js";
import {activatePromocode} from "../actions.js";

import "./activate_promocode.scss";

const HEADING = "FSD0000165";
const INPUT_PLACEHOLDER = "FSD0000166";
const ACTIVATION_NOTICE = "FSD0000167";
const PROMOCODE_PROCESSING = "FSD0000362";
const PROMOCODE_ACTIVATED = "FSD0000336";
const SUBMIT = "FSD0000168";

function NotActivated({onCodeChanged}) {

  const {translate} = useTranslation();
  const errorMessage = useSelector((state) => getPromocodeActivationErrorMessage(state));
  const [code, setCode] = useState('');

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onCodeChanged(code);
    }
  }, [code, onCodeChanged]);

  const onButtonClick = useCallback(() => {
    onCodeChanged(code);
  }, [code, onCodeChanged]);

  const onCodeChangeHandler = useCallback((event) => {
    const newVal = event?.target?.value;
    if (newVal) {
      setCode(newVal);
    }
  }, []);

  return (
    <div>
      <div className="labeled-field">
        <InputLabel className="labeled-field__input-label"><T code={HEADING} /></InputLabel>
        <TextInput
          placeholder={translate(INPUT_PLACEHOLDER)}
          value={code}
          error={errorMessage}
          onChange={onCodeChangeHandler}
          onKeyDown={handleKeyDown}
        />
        <ErrorMessage error={errorMessage} />
      </div>

      <div className="activate-promocode__notice">
        <T code={ACTIVATION_NOTICE} />
      </div>
      <Button secondary continue onClick={onButtonClick}>
        <T code={SUBMIT} />
      </Button>
    </div>
  );
}

NotActivated.propTypes = {
  onCodeChanged: PropTypes.func
};

function Activated({className}) {
  const formattedPromocodeSavedAmount = useSelector((state) => getFormattedPromocodeSavedAmount(state));

  return (
    <PriceLineup
      className={className}
      description={<T className="activate-promocode__success" code={PROMOCODE_ACTIVATED} />}
      formattedPrice={formattedPromocodeSavedAmount}
    />
  );
}

Activated.propTypes = {
  className: PropTypes.string,
};

export default function ActivatePromocode({className}) {
  const dispatch = useDispatch();
  const isPromocodeActivated = useSelector((state) => getIsPromocodeActivated(state));
  const combinedClassName = classnames("activate-promocode", className);

  const onActivateCode = useCallback((code) => {
    const promocode = code ? code.trim() : null;
    if (promocode) {
      dispatch(activatePromocode(promocode));
    }
  }, [dispatch]);

  if (isPromocodeActivated) {
    return (
      <Activated className={combinedClassName}/>
    );
  }

  return (
    <PriceBox className={combinedClassName}>
      <InlineLoadingIndicator typePrefix="RENT_CONTRACTS/RENT_CONTRACT" message={<T code={PROMOCODE_PROCESSING} />}>
        <NotActivated onCodeChanged={onActivateCode}/>
      </InlineLoadingIndicator>
    </PriceBox>
  );
}

ActivatePromocode.propTypes = {
  className: PropTypes.string,
};
